// Placeholder KO bracket for the FIFA WM 2026 — used by KOBracket.tsx
// when no real KO fixtures exist yet (i.e. the group phase is still on).
//
// The numbers (#73…#104) follow FIFA's official fixture numbering for 2026:
//   Group phase  #1   .. #72   (12 groups × 6 games)
//   R32          #73  .. #88   (16 games)
//   R16          #89  .. #96   (8 games)
//   QF           #97  .. #100  (4 games)
//   SF           #101 .. #102  (2 games)
//   3rd place    #103          (1 game)
//   Final        #104          (1 game)
//
// Slot labels follow the official FIFA pairings published with the bracket
// reveal. Each R32 slot is one of:
//   - "Sieger <X>"               winner of group X
//   - "Zweiter <X>"              runner-up of group X
//   - "Drittplatzierter <X/Y/…>" one of the best third-placed teams that
//                                ended up assigned to this slot
// Later rounds reference the previous round by game number ("Sieger #73"),
// "Verlierer #101" for the 3rd-place game etc.
//
// Shape mirrors what KOBracket consumes from the DB so the same rendering
// component can show real or placeholder games without branching deeply.

export interface PlaceholderGame {
  id: string;          // synthetic — KOBracket uses this as React key
  game_no: number;
  phase: "r32" | "r16" | "qf" | "sf" | "third" | "final";
  group_label: string;
  home_team: string;
  away_team: string;
  home_flag: string;
  away_flag: string;
  venue: null;
  kickoff_time: string; // ISO; KOBracket ignores it for placeholders
  home_score: null;
  away_score: null;
  is_finished: false;
  is_live: false;
  is_placeholder: true;
}

const PLACEHOLDER_FLAG = "❓";

function mk(
  no: number,
  phase: PlaceholderGame["phase"],
  home: string,
  away: string,
): PlaceholderGame {
  return {
    id: `placeholder-${no}`,
    game_no: no,
    phase,
    group_label: "KO",
    home_team: home,
    away_team: away,
    home_flag: PLACEHOLDER_FLAG,
    away_flag: PLACEHOLDER_FLAG,
    venue: null,
    kickoff_time: "",
    home_score: null,
    away_score: null,
    is_finished: false,
    is_live: false,
    is_placeholder: true,
  };
}

// R32 — Sechzehntelfinale (16 games, #73..#88).
// Pairings follow FIFA's official 2026 knockout bracket (Annex of the
// tournament regulations / FIFA match schedule). Each third-place slot lists
// the five groups one of whose third-placed teams can land there; the exact
// group only firms up once all 72 group games are played. These labels are a
// preview only and don't affect scoring — real fixtures replace them via the
// football-data sync once the pairings are known.
const R32: PlaceholderGame[] = [
  mk(73, "r32", "Zweiter A", "Zweiter B"),
  mk(74, "r32", "Sieger E", "Drittplatzierter A/B/C/D/F"),
  mk(75, "r32", "Sieger F", "Zweiter C"),
  mk(76, "r32", "Sieger C", "Zweiter F"),
  mk(77, "r32", "Sieger I", "Drittplatzierter C/D/F/G/H"),
  mk(78, "r32", "Zweiter E", "Zweiter I"),
  mk(79, "r32", "Sieger A", "Drittplatzierter C/E/F/H/I"),
  mk(80, "r32", "Sieger L", "Drittplatzierter E/H/I/J/K"),
  mk(81, "r32", "Sieger D", "Drittplatzierter B/E/F/I/J"),
  mk(82, "r32", "Sieger G", "Drittplatzierter A/E/H/I/J"),
  mk(83, "r32", "Zweiter K", "Zweiter L"),
  mk(84, "r32", "Sieger H", "Zweiter J"),
  mk(85, "r32", "Sieger B", "Drittplatzierter E/F/G/I/J"),
  mk(86, "r32", "Sieger J", "Zweiter H"),
  mk(87, "r32", "Sieger K", "Drittplatzierter D/E/I/J/L"),
  mk(88, "r32", "Zweiter D", "Zweiter G"),
];

// R16 — Achtelfinale (8 games, #89..#96). The R32 winners are paired per the
// official bracket — NOT sequentially (e.g. #89 = Sieger #74 vs Sieger #77).
const R16: PlaceholderGame[] = [
  mk(89, "r16", "Sieger #74", "Sieger #77"),
  mk(90, "r16", "Sieger #73", "Sieger #75"),
  mk(91, "r16", "Sieger #76", "Sieger #78"),
  mk(92, "r16", "Sieger #79", "Sieger #80"),
  mk(93, "r16", "Sieger #83", "Sieger #84"),
  mk(94, "r16", "Sieger #81", "Sieger #82"),
  mk(95, "r16", "Sieger #86", "Sieger #88"),
  mk(96, "r16", "Sieger #85", "Sieger #87"),
];

// QF — Viertelfinale (4 games, #97..#100), per the official bracket.
const QF: PlaceholderGame[] = [
  mk(97, "qf", "Sieger #89", "Sieger #90"),
  mk(98, "qf", "Sieger #93", "Sieger #94"),
  mk(99, "qf", "Sieger #91", "Sieger #92"),
  mk(100, "qf", "Sieger #95", "Sieger #96"),
];

// SF — Halbfinale (2 games, #101..#102).
const SF: PlaceholderGame[] = [
  mk(101, "sf", "Sieger #97", "Sieger #98"),
  mk(102, "sf", "Sieger #99", "Sieger #100"),
];

// Third place + final.
const THIRD: PlaceholderGame = mk(103, "third", "Verlierer #101", "Verlierer #102");
const FINAL: PlaceholderGame = mk(104, "final", "Sieger #101", "Sieger #102");

export const PLACEHOLDER_BRACKET: PlaceholderGame[] = [
  ...R32, ...R16, ...QF, ...SF, THIRD, FINAL,
];
